<template>
  <div class="confirm-sale-panel" :class="{ 'confirm-sale-panel--open': open }" :aria-hidden="!open">
    <div class="confirm-sale-panel__inner">
      <!-- Interactive header -->
      <div v-if="!readOnly" class="confirm-sale-panel__head">
        <span class="confirm-sale-panel__title">Confirm {{ outcomeLabel }}</span>
        <span class="confirm-sale-panel__price">{{ priceText }}</span>
      </div>

      <!-- Settled: the outcome, the price, the badge. No form, no way out. -->
      <div v-if="readOnly" class="confirm-sale-panel__settled">
        <span class="confirm-sale-panel__pill" :class="`confirm-sale-panel__pill--${outcomeModifier}`">
          {{ settledLabel }}
        </span>
        <div class="confirm-sale-panel__settled-block">
          <span class="confirm-sale-panel__field-label">Price</span>
          <span class="confirm-sale-panel__settled-price">{{ priceText }}</span>
        </div>
        <div class="confirm-sale-panel__settled-block">
          <span class="confirm-sale-panel__field-label">Winning badge</span>
          <span class="confirm-sale-panel__settled-badge">{{ badge || '—' }}</span>
        </div>
      </div>

      <!-- Collecting the badge, if this outcome needs one -->
      <form v-else class="confirm-sale-panel__body" @submit.prevent="confirm">
        <template v-if="floorSale">
          <label class="confirm-sale-panel__field-label" for="confirm-sale-panel-badge">Winning badge</label>
          <div class="confirm-sale-panel__field">
            <InputText
              id="confirm-sale-panel-badge"
              ref="badgeField"
              v-model="typedBadge"
              class="confirm-sale-panel__input"
              type="text"
              inputmode="numeric"
              autocomplete="off"
              placeholder="Enter badge…"
              @focus="(event) => event.target.select()"
            />
          </div>
        </template>

        <template v-else-if="onlineSale">
          <div class="confirm-sale-panel__winner">
            <span class="confirm-sale-panel__field-label">Winning badge</span>
            <span class="confirm-sale-panel__winner-badge">{{ badge }}</span>
          </div>

          <template v-if="overrideOpen">
            <label class="confirm-sale-panel__field-label" for="confirm-sale-panel-badge">
              Override winning badge
            </label>
            <div class="confirm-sale-panel__field">
              <InputText
                id="confirm-sale-panel-badge"
                ref="badgeField"
                v-model="typedBadge"
                class="confirm-sale-panel__input"
                type="text"
                inputmode="numeric"
                autocomplete="off"
                placeholder="Enter badge…"
                @focus="(event) => event.target.select()"
              />
            </div>
          </template>
          <Button
            v-else
            class="confirm-sale-panel__override"
            type="button"
            icon="pi pi-flag"
            label="Floor Override"
            @click="openOverride"
          />
        </template>

        <span v-else class="confirm-sale-panel__no-sale">
          This asset will be recorded as <strong>No Sale</strong> at {{ priceText }}.
        </span>
      </form>

      <span v-if="errorMessage && !readOnly" class="confirm-sale-panel__error">{{ errorMessage }}</span>

      <div v-if="!readOnly" class="confirm-sale-panel__footer">
        <Button
          class="confirm-sale-panel__action confirm-sale-panel__action--cancel"
          :disabled="submitting"
          label="Cancel"
          @click="emit('cancel')"
        />
        <Button
          class="confirm-sale-panel__action"
          :class="`confirm-sale-panel__action--${outcomeModifier}`"
          :disabled="!canConfirm"
          :label="`Confirm ${outcomeLabel}`"
          @click="confirm"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';

// The panel that slides up over the action zone when the clerk presses Sell,
// Offer or No Sale, and the last thing between a lot and a settled outcome.
//
// `bid` and `badge` are SNAPSHOTS from the moment the clerk pressed, not live
// state, and that is the point: what gets recorded is what the clerk saw when
// they decided. The application captures them, freezes bidding, and passes them
// in; this panel deliberately reads nothing live.
//
// It called `finalizeSale` and `resumeSale` itself. Both are events now, with
// `submitting` and `errorMessage` passed back in — so the panel stays open on a
// failed resume, which matters: a lot stranded paused with the panel dismissed
// has no way to be unstuck.
//
// Three modes, decided by who holds the high bid:
//   No Sale     — never collects a badge, even when the floor held the bid.
//   Floor sale  — REQUIRES one; the floor is a placeholder, not a buyer.
//   Online sale — shows the winner, with an override for naming a different one.
defineOptions({ name: 'ConfirmSalePanel' });

const props = defineProps({
  open: { type: Boolean, default: false },
  // SOLD | NOT-SOLD | OFFER — the outcome the clerk pressed.
  finalState: { type: String, default: null },
  // The high bid at press time.
  bid: { type: Number, default: null },
  // The high bidder's badge at press time. 'FLOOR' is the runtime's placeholder
  // for a bid the auctioneer took off the floor, which is what makes a sale a
  // floor sale.
  badge: { type: String, default: null },
  // A settled lot: the outcome, read-only, with nothing to press.
  readOnly: { type: Boolean, default: false },
  // A confirm or a cancel is in flight.
  submitting: { type: Boolean, default: false },
  // What went wrong, if it did.
  errorMessage: { type: String, default: null },
});

const emit = defineEmits(['confirm', 'cancel']);

const typedBadge = ref('');
const overrideOpen = ref(false);
const badgeField = ref(null);

const OUTCOME_LABELS = { SOLD: 'Sell', 'NOT-SOLD': 'No Sale', OFFER: 'Offer' };
const SETTLED_LABELS = { SOLD: 'Sold', 'NOT-SOLD': 'No Sale', OFFER: 'Offer' };
const OUTCOME_MODIFIERS = { SOLD: 'sell', 'NOT-SOLD': 'no-sale', OFFER: 'offer' };

const outcomeLabel = computed(() => OUTCOME_LABELS[props.finalState] ?? '');
const settledLabel = computed(() => SETTLED_LABELS[props.finalState] ?? '');
// The confirm button wears the same tone as the ActionGrid button that opened
// the panel, so the clerk is finishing the action they started.
const outcomeModifier = computed(() => OUTCOME_MODIFIERS[props.finalState] ?? 'neutral');
const priceText = computed(() => `$${(props.bid || 0).toLocaleString()}`);

const noSale = computed(() => props.finalState === 'NOT-SOLD');
const floorSale = computed(() => !noSale.value && String(props.badge).toUpperCase() === 'FLOOR');
const onlineSale = computed(() => !noSale.value && !floorSale.value);

const collectingBadge = computed(() => floorSale.value || overrideOpen.value);
const canConfirm = computed(
  () => !props.submitting && (noSale.value || !collectingBadge.value || typedBadge.value.trim() !== '')
);

// `preventScroll` is load-bearing. The panel is an absolute overlay inside a
// clipped row, and focusing an input inside it otherwise makes the browser
// scroll that row into view — which yanks the on-block panel sideways behind the
// panel and leaves it there.
const focusBadgeField = () => nextTick(() => badgeField.value?.$el?.focus({ preventScroll: true }));

function openOverride() {
  overrideOpen.value = true;
  focusBadgeField();
}

function confirm() {
  if (!canConfirm.value) return;
  if (noSale.value) {
    emit('confirm', { finalState: props.finalState, bid: props.bid, badge: undefined });
    return;
  }
  const badge = collectingBadge.value ? typedBadge.value.trim() : props.badge;
  emit('confirm', { finalState: props.finalState, bid: props.bid, badge });
}

const onKeydown = (event) => {
  if (event.key === 'Escape' && !props.submitting) emit('cancel');
};

watch(
  () => props.open,
  (isOpen) => {
    // A settled lot is not closable and collects nothing, so it neither resets
    // nor binds Esc — pressing Escape on a sold lot must not look like an undo.
    if (!isOpen || props.readOnly) {
      window.removeEventListener('keydown', onKeydown);
      return;
    }
    typedBadge.value = '';
    overrideOpen.value = false;
    window.addEventListener('keydown', onKeydown);
    if (floorSale.value) focusBadgeField();
  }
);

onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown));
</script>

<style scoped>
.confirm-sale-panel {
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transform: translateY(100%);
  pointer-events: none;
  transition: transform 280ms cubic-bezier(0.22, 1, 0.36, 1);
  background: var(--p-confirm-sale-panel-background);
  border: var(--p-confirm-sale-panel-border-width) solid var(--p-confirm-sale-panel-border-color);
  border-radius: var(--p-confirm-sale-panel-border-radius);
}

/* The shadow is tied to the open state. Closed, the panel sits translated below
   the action zone, and an upward shadow there bleeds a dark band across it. */
.confirm-sale-panel--open {
  transform: translateY(0);
  pointer-events: auto;
  box-shadow: var(--p-confirm-sale-panel-box-shadow);
}

.confirm-sale-panel__inner {
  flex: 1;
  min-height: 0;
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--p-confirm-sale-panel-inner-gap);
  padding: var(--p-confirm-sale-panel-inner-padding);
}

.confirm-sale-panel__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}

.confirm-sale-panel__title {
  font-size: var(--p-confirm-sale-panel-title-font-size);
  font-weight: var(--p-confirm-sale-panel-title-font-weight);
  color: var(--p-confirm-sale-panel-title-color);
}

.confirm-sale-panel__price {
  font-family: var(--p-confirm-sale-panel-price-font-family);
  font-size: var(--p-confirm-sale-panel-price-font-size);
  font-weight: var(--p-confirm-sale-panel-price-font-weight);
  color: var(--p-confirm-sale-panel-price-color);
  font-variant-numeric: tabular-nums;
}

.confirm-sale-panel__body {
  display: flex;
  flex-direction: column;
  gap: var(--p-confirm-sale-panel-body-gap);
}

.confirm-sale-panel__field-label {
  text-transform: uppercase;
  font-size: var(--p-confirm-sale-panel-field-label-font-size);
  font-weight: var(--p-confirm-sale-panel-field-label-font-weight);
  letter-spacing: var(--p-confirm-sale-panel-field-label-letter-spacing);
  color: var(--p-confirm-sale-panel-field-label-color);
}

/* ── Settled ────────────────────────────────────────────────────────────── */

.confirm-sale-panel__settled {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: var(--p-confirm-sale-panel-settled-gap);
}

.confirm-sale-panel__pill {
  text-transform: uppercase;
  line-height: 1;
  padding: var(--p-confirm-sale-panel-pill-padding);
  border-radius: var(--p-confirm-sale-panel-pill-border-radius);
  font-size: var(--p-confirm-sale-panel-pill-font-size);
  font-weight: var(--p-confirm-sale-panel-pill-font-weight);
  letter-spacing: var(--p-confirm-sale-panel-pill-letter-spacing);
}

.confirm-sale-panel__pill--sell {
  background: var(--p-confirm-sale-panel-sell-background);
  color: var(--p-confirm-sale-panel-sell-color);
}

.confirm-sale-panel__pill--no-sale {
  background: var(--p-confirm-sale-panel-no-sale-background);
  color: var(--p-confirm-sale-panel-no-sale-color);
}

.confirm-sale-panel__pill--offer {
  background: var(--p-confirm-sale-panel-offer-background);
  color: var(--p-confirm-sale-panel-offer-color);
}

.confirm-sale-panel__pill--neutral {
  background: var(--p-confirm-sale-panel-neutral-background);
  color: var(--p-confirm-sale-panel-neutral-color);
}

.confirm-sale-panel__settled-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--p-confirm-sale-panel-settled-block-gap);
}

.confirm-sale-panel__settled-price {
  font-family: var(--p-confirm-sale-panel-settled-price-font-family);
  font-size: var(--p-confirm-sale-panel-settled-price-font-size);
  font-weight: var(--p-confirm-sale-panel-settled-price-font-weight);
  line-height: var(--p-confirm-sale-panel-settled-price-line-height);
  letter-spacing: var(--p-confirm-sale-panel-settled-price-letter-spacing);
  color: var(--p-confirm-sale-panel-settled-price-color);
  font-variant-numeric: tabular-nums;
}

.confirm-sale-panel__settled-badge {
  font-family: var(--p-confirm-sale-panel-settled-badge-font-family);
  font-size: var(--p-confirm-sale-panel-settled-badge-font-size);
  font-weight: var(--p-confirm-sale-panel-settled-badge-font-weight);
  line-height: var(--p-confirm-sale-panel-settled-badge-line-height);
  color: var(--p-confirm-sale-panel-settled-badge-color);
  font-variant-numeric: tabular-nums;
}

/* ── Badge entry ────────────────────────────────────────────────────────── */

.confirm-sale-panel__field {
  display: flex;
  align-items: center;
  padding: var(--p-confirm-sale-panel-field-padding);
  border: var(--p-confirm-sale-panel-field-border-width) solid var(--p-confirm-sale-panel-field-border-color);
  border-radius: var(--p-confirm-sale-panel-field-border-radius);
  background: var(--p-confirm-sale-panel-field-background);
}

.confirm-sale-panel__field :deep(.confirm-sale-panel__input),
.confirm-sale-panel__field :deep(.confirm-sale-panel__input:enabled:focus) {
  flex: 1;
  width: 100%;
  min-width: 0;
  border: 0;
  outline: 0;
  box-shadow: none;
  background: transparent;
  font-variant-numeric: tabular-nums;
  font-family: var(--p-confirm-sale-panel-input-font-family);
  font-size: var(--p-confirm-sale-panel-input-font-size);
  font-weight: var(--p-confirm-sale-panel-input-font-weight);
  letter-spacing: var(--p-confirm-sale-panel-input-letter-spacing);
  line-height: var(--p-confirm-sale-panel-input-line-height);
  color: var(--p-confirm-sale-panel-input-color);
  padding: var(--p-confirm-sale-panel-input-padding);
}

.confirm-sale-panel__winner {
  display: flex;
  flex-direction: column;
  gap: var(--p-confirm-sale-panel-winner-gap);
}

.confirm-sale-panel__winner-badge {
  font-family: var(--p-confirm-sale-panel-winner-badge-font-family);
  font-size: var(--p-confirm-sale-panel-winner-badge-font-size);
  font-weight: var(--p-confirm-sale-panel-winner-badge-font-weight);
  color: var(--p-confirm-sale-panel-winner-badge-color);
  font-variant-numeric: tabular-nums;
}

.p-button.confirm-sale-panel__override {
  align-self: flex-start;
  margin-top: 4px;
  font-family: inherit;
  gap: var(--p-confirm-sale-panel-override-gap);
  padding: var(--p-confirm-sale-panel-override-padding);
  border-radius: var(--p-confirm-sale-panel-override-border-radius);
  font-size: var(--p-confirm-sale-panel-override-font-size);
  font-weight: var(--p-confirm-sale-panel-override-font-weight);
  letter-spacing: var(--p-confirm-sale-panel-override-letter-spacing);
  background: var(--p-confirm-sale-panel-override-background);
  border: var(--p-confirm-sale-panel-override-border-width) solid var(--p-confirm-sale-panel-override-border-color);
  color: var(--p-confirm-sale-panel-override-color);
}

.confirm-sale-panel__no-sale {
  font-size: var(--p-confirm-sale-panel-no-sale-font-size);
  line-height: var(--p-confirm-sale-panel-no-sale-line-height);
  color: var(--p-confirm-sale-panel-no-sale-text-color);
}

.confirm-sale-panel__no-sale strong {
  color: var(--p-confirm-sale-panel-no-sale-strong-color);
}

.confirm-sale-panel__error {
  font-size: var(--p-confirm-sale-panel-error-font-size);
  font-weight: var(--p-confirm-sale-panel-error-font-weight);
  color: var(--p-confirm-sale-panel-error-color);
}

/* ── Footer ─────────────────────────────────────────────────────────────── */

.confirm-sale-panel__footer {
  margin-top: auto;
  display: flex;
  justify-content: flex-end;
  gap: var(--p-confirm-sale-panel-footer-gap);
}

.p-button.confirm-sale-panel__action {
  justify-content: center;
  min-width: 160px;
  text-transform: uppercase;
  transition: all 140ms cubic-bezier(0.2, 0, 0, 1);
  border-style: solid;
  border-width: var(--p-confirm-sale-panel-action-border-width);
  border-radius: var(--p-confirm-sale-panel-action-border-radius);
  padding: var(--p-confirm-sale-panel-action-padding);
  font-weight: var(--p-confirm-sale-panel-action-font-weight);
  letter-spacing: var(--p-confirm-sale-panel-action-letter-spacing);
}

.p-button.confirm-sale-panel__action:disabled {
  opacity: 0.42;
  box-shadow: none;
}

.p-button.confirm-sale-panel__action--cancel {
  background: var(--p-confirm-sale-panel-cancel-background);
  border-color: var(--p-confirm-sale-panel-cancel-border-color);
  color: var(--p-confirm-sale-panel-cancel-color);
}

.p-button.confirm-sale-panel__action--sell {
  background: var(--p-confirm-sale-panel-sell-background);
  border-color: var(--p-confirm-sale-panel-sell-border-color);
  color: var(--p-confirm-sale-panel-sell-color);
}

.p-button.confirm-sale-panel__action--no-sale {
  background: var(--p-confirm-sale-panel-no-sale-background);
  border-color: var(--p-confirm-sale-panel-no-sale-border-color);
  color: var(--p-confirm-sale-panel-no-sale-color);
}

.p-button.confirm-sale-panel__action--offer {
  background: var(--p-confirm-sale-panel-offer-background);
  border-color: var(--p-confirm-sale-panel-offer-border-color);
  color: var(--p-confirm-sale-panel-offer-color);
}

.p-button.confirm-sale-panel__action--neutral {
  background: var(--p-confirm-sale-panel-neutral-background);
  border-color: var(--p-confirm-sale-panel-neutral-border-color);
  color: var(--p-confirm-sale-panel-neutral-color);
}
</style>
